import { useCallback, useEffect, useRef, useState } from 'react';
import type { AdminStats, AuditLog } from '@kome/shared';
import { formatListTime } from '@kome/shared';
import adminApi, { AdminApiError } from '@/lib/api';
import TopBar from '@/components/TopBar';
import StatCard from '@/components/StatCard';
import Card from '@/components/Card';
import Spinner from '@/components/Spinner';
import Badge from '@/components/Badge';
import Button from '@/components/Button';

/** 대시보드 자동 새로고침 주기 (15초) */
const POLL_INTERVAL_MS = 15_000;
/** 최근 감사 로그 표시 개수 */
const RECENT_AUDIT_LIMIT = 8;

/** 바이트를 사람이 읽기 쉬운 단위로 변환 (저장용량 표시용) */
function formatBytes(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  const formatted = i === 0 ? String(value) : value.toFixed(value >= 100 ? 0 : 1);
  return `${formatted} ${units[i]}`;
}

/** 천 단위 구분 숫자 */
function formatNumber(n: number): string {
  return (n ?? 0).toLocaleString('ko-KR');
}

export default function Dashboard() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 마운트 여부 추적 (언마운트 후 setState 방지)
  const mountedRef = useRef(true);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (opts.silent) setRefreshing(true);
    try {
      const [statsRes, auditRes] = await Promise.all([
        adminApi.stats(),
        adminApi.audit({ limit: RECENT_AUDIT_LIMIT, offset: 0 }),
      ]);
      if (!mountedRef.current) return;
      setStats(statsRes.stats);
      setLogs(auditRes.logs);
      setError(null);
    } catch (e) {
      if (!mountedRef.current) return;
      // 401 은 api 클라이언트가 세션을 폐기하므로 가드가 로그인으로 보낸다.
      if (e instanceof AdminApiError && e.status === 401) return;
      setError(e instanceof Error ? e.message : '현황을 불러오지 못했습니다.');
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  // 최초 로드 + 15초 폴링
  useEffect(() => {
    mountedRef.current = true;
    void load();
    const timer = window.setInterval(() => {
      // 문서가 숨겨져 있으면(백그라운드 탭) 폴링 생략 — 가벼움 원칙
      if (document.visibilityState === 'visible') void load({ silent: true });
    }, POLL_INTERVAL_MS);
    return () => {
      mountedRef.current = false;
      window.clearInterval(timer);
    };
  }, [load]);

  const generatedLabel = stats
    ? `${formatListTime(stats.generatedAt)} 기준`
    : '';

  return (
    <>
      <TopBar
        title="대시보드"
        subtitle="서비스 현황 한눈에 보기"
        actions={
          <div className="row">
            {generatedLabel && <span className="muted">{generatedLabel}</span>}
            {refreshing && <Spinner />}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void load({ silent: true })}
              loading={refreshing && !loading}
            >
              새로고침
            </Button>
          </div>
        }
      />
      <div className="admin-content">
        {loading && !stats ? (
          <Spinner center />
        ) : error && !stats ? (
          <Card title="오류">
            <div className="muted" style={{ marginBottom: 'var(--space-md)' }}>
              {error}
            </div>
            <Button variant="primary" onClick={() => void load()}>
              다시 시도
            </Button>
          </Card>
        ) : (
          <>
            {error && (
              <div className="muted" style={{ marginBottom: 'var(--space-lg)' }}>
                ⚠ 최신 데이터 갱신 실패: {error}
              </div>
            )}

            {stats && (
              <section className="page-section">
                <div className="stat-grid">
                  <StatCard
                    label="총 사용자"
                    icon="👥"
                    value={formatNumber(stats.totalUsers)}
                    hint="전체 발급 계정"
                  />
                  <StatCard
                    label="활성 사용자"
                    icon="✅"
                    value={formatNumber(stats.activeUsers)}
                    hint="비활성 제외"
                  />
                  <StatCard
                    label="접속 중"
                    icon="🟢"
                    value={formatNumber(stats.onlineNow)}
                    hint="현재 온라인"
                  />
                  <StatCard
                    label="채팅방"
                    icon="💬"
                    value={formatNumber(stats.totalRooms)}
                    hint="1:1 + 단톡"
                  />
                  <StatCard
                    label="오늘 메시지"
                    icon="✉️"
                    value={formatNumber(stats.messagesToday)}
                    hint="자정 이후 전송"
                  />
                  <StatCard
                    label="누적 메시지"
                    icon="📨"
                    value={formatNumber(stats.messagesTotal)}
                    hint="전체 기간"
                  />
                  <StatCard
                    label="오늘 AI 호출"
                    icon="🤖"
                    value={formatNumber(stats.aiCallsToday)}
                    hint="Claude 요청 수"
                  />
                  <StatCard
                    label="저장 용량"
                    icon="🗄️"
                    value={formatBytes(stats.storageBytes)}
                    hint="첨부파일(R2)"
                  />
                </div>
              </section>
            )}

            <section className="page-section">
              <Card title="최근 감사 로그">
                {logs.length === 0 ? (
                  <div className="muted">기록된 관리자 활동이 없습니다.</div>
                ) : (
                  <ul className="audit-mini">
                    {logs.map((log) => (
                      <li key={log.id} className="audit-mini__item">
                        <Badge tone="info">{log.action}</Badge>
                        <span className="audit-mini__actor">{log.actor}</span>
                        {log.target && (
                          <span className="audit-mini__target muted">{log.target}</span>
                        )}
                        {log.detail && (
                          <span className="audit-mini__detail muted">{log.detail}</span>
                        )}
                        <span className="audit-mini__time muted">
                          {formatListTime(log.createdAt)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            </section>
          </>
        )}
      </div>

      {/* 대시보드 전용 보조 스타일 (감사 로그 미니 리스트) */}
      <style>{`
        .audit-mini {
          list-style: none;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
        }
        .audit-mini__item {
          display: flex;
          align-items: center;
          gap: var(--space-sm);
          padding: 10px 0;
          border-bottom: 1px solid var(--color-divider);
          font-size: var(--font-size-sm);
          min-width: 0;
        }
        .audit-mini__item:last-child {
          border-bottom: none;
        }
        .audit-mini__actor {
          font-weight: 500;
          flex-shrink: 0;
        }
        .audit-mini__target {
          flex-shrink: 0;
        }
        .audit-mini__detail {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .audit-mini__time {
          margin-left: auto;
          flex-shrink: 0;
          font-size: var(--font-size-xs);
        }
      `}</style>
    </>
  );
}
